import { RecordContextProvider } from "react-admin";
import { TabContent } from "@/components/common/PageLayout";
import { useResourcePermissions } from "@/hooks/useResourcePermissions";
import { ResourceName } from "@/types";
import { DATE_FORMATS, formatDate } from "@/utils/formatters/dateFormatter";
import { policyDisplayLabel } from "@/resources/stoploss-claims/hooks/useStoplossClaimContext";
import {
  MEMBER_IDENTIFIERS_NOTE,
  dependentEligibilityFields,
  employeeEligibilityFields,
  useClaimantWithFlattenedPolicy,
  type ClaimantWithFlattenedPolicy,
} from "../ClaimantOverviewTab";
import { useSelectedPolicyData } from "../../hooks/useCurrentPolicyData";
import { DetailsSection, type ValueOverrides } from "./DetailsSection";
import {
  dependentEnrollmentFields,
  employeeEnrollmentFields,
  identityFields,
  memberIdentifierFields,
} from "./detailsFields";

/** Read mode folds the age into the birth date ("May 3, 1979 (47)"); the grid keeps both fields. */
const identityReadFields = identityFields.filter((f) => f.source !== "claimant_age");

const identityOverrides: ValueOverrides = {
  claimant_birth_date: (record) => {
    const { claimant_birth_date: dob, claimant_age: age } = record as ClaimantWithFlattenedPolicy;
    if (!dob) return "—";
    const date = formatDate(dob, DATE_FORMATS.DISPLAY_LONG);
    return typeof age === "number" ? `${date} (${age})` : date;
  },
};

/**
 * Details tab (#7660): the Overview tab's facts, read-first, with an Edit per
 * section that turns on the same in-place editors. Identity and member
 * identifiers are claimant-level; enrollment and eligibility are per selected
 * policy and absent when none is selected.
 *
 * The flattened record (`status.*`, `enrollment.*`, `_selectedPolicyId`) is put
 * in record context exactly as the Overview tab does, so the grids' editors
 * save to the selected policy.
 */
export const ClaimantDetailsTab = () => {
  const claimantWithPolicy = useClaimantWithFlattenedPolicy();
  const { policy, policyId } = useSelectedPolicyData();
  const { canEdit } = useResourcePermissions(ResourceName.CLAIMANTS);

  if (!claimantWithPolicy) return null;

  const isDependent = claimantWithPolicy.enrollment?.is_dependent ?? false;
  const policyLabel = policyDisplayLabel(policy);

  return (
    <RecordContextProvider value={claimantWithPolicy}>
      <TabContent>
        <DetailsSection
          title="Identity"
          name="identity"
          fields={identityFields}
          readFields={identityReadFields}
          valueOverrides={identityOverrides}
          record={claimantWithPolicy}
          canEdit={canEdit}
        />
        <DetailsSection
          title="Member Identifiers"
          name="member identifiers"
          fields={memberIdentifierFields}
          record={claimantWithPolicy}
          canEdit={canEdit}
          note={MEMBER_IDENTIFIERS_NOTE}
          emptyText="No member identifiers recorded."
        />
        {policyId && (
          <DetailsSection
            title={`Enrollment · ${policyLabel}`}
            name="enrollment"
            fields={isDependent ? dependentEnrollmentFields : employeeEnrollmentFields}
            record={claimantWithPolicy}
            canEdit={canEdit}
            emptyText="No enrollment information recorded for this policy."
          />
        )}
        {policyId && (
          <DetailsSection
            title={`Eligibility · ${policyLabel}`}
            name="eligibility"
            fields={isDependent ? dependentEligibilityFields : employeeEligibilityFields}
            record={claimantWithPolicy}
            canEdit={canEdit}
            collapseEmptyGroups
            emptyText="No eligibility information recorded for this policy."
          />
        )}
      </TabContent>
    </RecordContextProvider>
  );
};
